import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as AccountsService from '../services/AccountsService';
import { Account } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const accountsKeys = {
    all: ['accounts'] as const,
    lists: () => [...accountsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...accountsKeys.lists(), { filters }] as const,
    details: () => [...accountsKeys.all, 'detail'] as const,
    detail: (id: string) => [...accountsKeys.details(), id] as const,
};

// Hooks
export const useAccountsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: accountsKeys.list(filters),
        queryFn: () => AccountsService.fetchRecords(filters),
    });
};

export const useAccountQuery = (id: string) => {
    return useQuery({
        queryKey: accountsKeys.detail(id),
        queryFn: () => AccountsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateAccountMutation = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: AccountsService.add,
        onSuccess: () => {
            notifySuccess('Account created');
            queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateAccountMutation = () => {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Account }) =>
            AccountsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Account updated');
            queryClient.invalidateQueries({ queryKey: accountsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: accountsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: AccountsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Account deleted');
            queryClient.invalidateQueries({ queryKey: accountsKeys.all });
        },
        onError: notifyError,
    });
};

// FNP-383: admin Restore flips a PENDING_DELETION account back to ACTIVE.
// The backend (FNP-382) emits an EventBridge event that workflows-api
// (FNP-381) consumes to re-enable the Cognito user — invalidating the
// account list + detail caches surfaces the new state once the cascade
// completes (typically sub-second in dev).
export const useRestoreAccountMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: AccountsService.restoreRecord,
        onSuccess: () => {
            notifySuccess('Account restored');
            queryClient.invalidateQueries({ queryKey: accountsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {accountStatus: 'ACTIVE'};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

// Filter state management (shared state instead of local)
export const useAccountsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    // Subscribe to filter changes
    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};